import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Loader from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import { toastMessage } from '../../utils/toast';
import * as reportActions from '../../Redux/Actions/report';
import { clearSession } from '../../utils/initiate';
import Warning from '../Ui/400';
import { AdminJobsChart, SLAJobsChart, RecentJobs, Metrics, HorizontalBarChart } from './chart';
import JobsCount from './jobsCount';
import FlyerModal from './flyerModal';
import { getFlyerNotifications, updateFlyerNotification } from '../../Redux/Actions/notification';

const Dashboard = ({ history }) => {

    const [loading, setLoading] = useState(false);
    const [jobsCountData, setJobsCountData] = useState([]);
    const [corporateJobsCountData, setCorporateJobsCountData] = useState([]);
    const [adminJobsData, setAdminJobsData] = useState([]);
    const [recentJobsJobsData, setRecentJobsJobsData] = useState([]);
    const [metricesData, setMetricesData] = useState([]);
    const [slaJobsData, setSlaJobsData] = useState([]);
    const [jobsDailyCount, setJobsDailyCount] = useState([]);
    const [flyerNotificationConfigs, setFlyerNotificationConfigs] = useState({
        data: [],
        url: null,
        id: null, 
        loading: false
    });

    const [errStatus, setErrStatus] = useState(200);
    const userData = useSelector(({User}) => User.data);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        let payload = {
            userId: userData?.UserId,
            statusType: "UnRead",
        };
        getFlyerNotification(payload);
    
    }, [userData]);

    const getFlyerNotification = (payload) => {
        getFlyerNotifications(payload).then(res => {
            if(res?.data) {
                setFlyerNotificationConfigs({
                    data: res?.data,
                    url: res?.data?.[0]?.documentURL,
                    id: res?.data?.[0]?.id
                });
            }
        });
    };

    const updateStatus = (id) => {
        setFlyerNotificationConfigs((preState) => ({...preState, loading: true }));
        let payload = {
            notificationIds : [id]
        }
        updateFlyerNotification(payload).then(() => {
            const updatedItems = [...flyerNotificationConfigs.data];
            updatedItems.shift();
            setFlyerNotificationConfigs({ data: updatedItems, url: updatedItems[0]?.documentURL, id: updatedItems[0]?.id, loading: false });
        }).catch((error) => {
            console.log(error);
            setFlyerNotificationConfigs((preState) => ({...preState, loading: false }));
        });
    }

    useEffect(() => {
        setLoading(true);

        reportActions.getAllAdminDashboardJobs().then(res => {
            if(res && res.data){
                setAdminJobsData(res.data);
            }else{
                toastMessage('error', (res && res.message) || 'Unable to load the Jobs');
            }
            setLoading(false);
        }).catch(err => {
            setErrStatus(err.response.status);
            (err.response.status === 401) && clearSession(dispatch, history);
        });

        reportActions.getAllSLAForJobs().then(res => {
            if(res && res.data){
                setSlaJobsData(res.data);
            }else{
                toastMessage('error', (res && res.message) || 'Unable to load the sla jobs');
            }
            setLoading(false);
        }).catch(err => {
            setErrStatus(err.response.status);
            (err.response.status === 401) && clearSession(dispatch, history);
        });

        reportActions.getAllRecentJobs().then(res => {
            if(res && res.data){
                setRecentJobsJobsData(res.data);
            }else{
                toastMessage('error', (res && res.message) || 'Unable to load the recent jobs');
            }
            setLoading(false);
        }).catch(err => {
            setErrStatus(err.response.status);
            (err.response.status === 401) && clearSession(dispatch, history);
        });

        reportActions.getAllMetrices().then(res => {
            if(res && res.data){
                setMetricesData(res.data);
            }else{
                toastMessage('error', (res && res.message) || 'Unable to load the jobs metrics');
            }
            setLoading(false);
        }).catch(err => {
            setErrStatus(err.response.status);
            (err.response.status === 401) && clearSession(dispatch, history);
        });

        reportActions.getJobsCount().then(res => {
            if(res && res.data){
                setJobsCountData(res.data);
            }else{
                toastMessage('error', (res && res.message) || 'Unable to load the jobs count');
            }
            setLoading(false);
        }).catch(err => {
            setErrStatus(err.response.status);
            (err.response.status === 401) && clearSession(dispatch, history);
        });

        reportActions.getJobsCount("corporateJob").then(res => {
            if(res && res.data){
                setCorporateJobsCountData(res.data);
            }else{
                toastMessage('error', (res && res.message) || 'Unable to load the jobs count');
            }
            setLoading(false);
        }).catch(err => {
            setErrStatus(err.response.status);
            (err.response.status === 401) && clearSession(dispatch, history);
        });

        // reportActions.getWeeklyJobsCount().then(res => {
        //     if(res && res.data){
        //         setJobsWeeklyCount(res.data);
        //     }else{
        //         toastMessage('error', (res && res.message) || 'Unable to load the jobs count');
        //     }
        //     setLoading(false);
        // }).catch(err => {
        //     setErrStatus(err.response.status);
        //     (err.response.status === 401) && clearSession(dispatch, history);
        // });

        reportActions.getJobCountsForActionRequested().then(res => {
            if(res && res.data){
                setJobsDailyCount(res.data);
            }else{
                toastMessage('error', (res && res.message) || 'Unable to load the jobs count');
            }
            setLoading(false);
        }).catch(err => {
            setErrStatus(err.response.status);
            (err.response.status === 401) && clearSession(dispatch, history);
        });
    }, [dispatch, history, userData]);

    if(errStatus === 400){
        return <Warning history={history} />;
    }

    return(
        <div className="container-fluid">
            <div className="page-title-box">
                <h4 className="page-title">{t("dashboard.title")}</h4>
            </div>
            <>
                {loading ? <div className="d-flex align-items-center justify-content-center h-100vh"><Loader type="ThreeDots" color="#2d207d" height={40} width={40} /></div>
                    : <div>
                        <div className="card row flex-row col-lg-12 mb-4">
                            <h4 style={{marginTop: '15px', marginLeft: '15px'}}>{t("dashboard.overAll")}</h4>
                            <div className="col-lg-12">
                                <JobsCount data={jobsCountData} history={history} />
                            </div>
                        </div>

                        <div className="card row flex-row col-lg-12 mb-4">
                            <h4 style={{marginTop: '15px', marginLeft: '15px'}}>{t("Corporate Jobs")}</h4>
                            <div className="col-lg-12">
                                <JobsCount data={corporateJobsCountData} history={history} page={'jobs-request'}/>
                            </div>
                        </div>
                        {/* <div className="card row flex-row col-lg-12 mb-4">
                            <h4 style={{marginTop: '15px', marginLeft: '15px'}}>{t("dashboard.weekly")}</h4>
                            <div className="col-lg-12">
                                <JobsCount data={jobsWeeklyCount} history={history} />
                            </div>
                        </div> */}
                        <div className="card row flex-row col-lg-12 mb-4">
                            <h4 style={{marginTop: '15px', marginLeft: '15px'}}>{t("dashboard.approvalRequests")}</h4>
                            <div className="col-lg-12">
                                <JobsCount data={jobsDailyCount} history={history} />
                            </div>
                        </div>
                        <div className="card row flex-row col-lg-12 mb-4">
                            <div className="col-lg-6">
                                <AdminJobsChart data={adminJobsData} name={t("dashboard.jobsStatus")} />
                            </div>
                            {(slaJobsData.length !== 0) ? <div className="col-lg-6">
                                <SLAJobsChart data={slaJobsData.slaInfor} avgTime={slaJobsData.averageTime} name={t("dashboard.jobsTurnaroundTime")} />
                            </div> : null}
                        </div>

                        <div className="card row flex-row col-lg-12 mb-4 pb-4">
                            <div className="col-lg-6">
                                <HorizontalBarChart data={[{key: 'Total Jobs', value: metricesData.numberOfJobs}, {key: 'Active Jobs', value: metricesData.numberOfActiveJobs}, {key: 'Total Staffs', value: metricesData.numberOfStaffs}]} name={t("dashboard.otherMetrics")} />
                            </div>
                            <div className="col-lg-6">
                                <Metrics data={metricesData.staffJobCount} name={t("dashboard.jobsStaffMetrics")} />
                            </div>
                        </div>

                        <div className="card row flex-row col-lg-12 mb-4 pb-4">
                            <div className="col-lg-6">
                                <RecentJobs data={recentJobsJobsData.recentJobProcesses} name={t("dashboard.recentJobProcess")} />
                            </div>
                            <div className="col-lg-6">
                                <RecentJobs data={recentJobsJobsData.recentJobStatuses} name={t("dashboard.recentJobStatus")} />
                            </div>
                        </div>
                    </div>
                }
            </>
            {flyerNotificationConfigs.data?.length ?
                <FlyerModal 
                    id={flyerNotificationConfigs?.id} 
                    url={flyerNotificationConfigs?.url}
                    updateStatus={updateStatus}
                    loading={flyerNotificationConfigs.loading}
                /> : null
            }
        </div>
    );
};

export default Dashboard;